import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Film, Upload, Loader2, Search, Music, Image as ImageIcon, Palette } from 'lucide-react';
import toast from 'react-hot-toast';
import { videoStudioService } from '../../services/videoStudioService';
import { imageStudioService } from '../../services/imageStudioService';
import { GeneratedVideo } from '../../types/videoStudio';

type AssetKind = 'all' | 'video' | 'audio' | 'image';

interface Props {
  onPick: (asset: GeneratedVideo) => void;
  onOpenGallery?: (source: 'video' | 'image') => void;
  refreshKey?: number;
}

const detectKind = (asset: GeneratedVideo): 'video' | 'audio' | 'image' => {
  const m = asset.mime_type || '';
  if (m.startsWith('audio/')) return 'audio';
  if (m.startsWith('image/')) return 'image';
  return 'video';
};

// Captura 1 frame do vídeo local (antes mesmo do upload terminar de processar)
// pra usar como thumbnail — sem isso, cada card da biblioteca/galeria precisa
// baixar o metadata do vídeo inteiro só pra desenhar a capa.
const captureVideoThumb = (file: File): Promise<Blob | null> => new Promise((resolve) => {
  const url = URL.createObjectURL(file);
  const v = document.createElement('video');
  v.preload = 'auto'; v.muted = true; v.playsInline = true;
  let done = false;
  const finish = (b: Blob | null) => {
    if (done) return; done = true;
    clearTimeout(timer); URL.revokeObjectURL(url); resolve(b);
  };
  const timer = setTimeout(() => finish(null), 8000);
  v.onloadeddata = () => {
    try { v.currentTime = Math.min(1, (v.duration || 2) / 2); } catch { finish(null); }
  };
  v.onseeked = () => {
    try {
      const scale = Math.min(1, 480 / (v.videoWidth || 480));
      const canvas = document.createElement('canvas');
      canvas.width = Math.max(2, Math.round((v.videoWidth || 480) * scale));
      canvas.height = Math.max(2, Math.round((v.videoHeight || 270) * scale));
      canvas.getContext('2d')!.drawImage(v, 0, 0, canvas.width, canvas.height);
      canvas.toBlob(b => finish(b), 'image/jpeg', 0.75);
    } catch { finish(null); }
  };
  v.onerror = () => finish(null);
  v.src = url;
});

const MediaLibrary: React.FC<Props> = ({ onPick, onOpenGallery, refreshKey }) => {
  const { t } = useTranslation();
  const [assets, setAssets] = useState<GeneratedVideo[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState<AssetKind>('all');
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await videoStudioService.listVideos({ limit: 60 });
      if (r.success) setAssets((r.data || []).filter(v => v.original_url));
    } catch (e: any) {
      // Sem isto, falha de rede virava unhandled rejection e a biblioteca
      // aparecia vazia como se o usuário não tivesse mídia nenhuma.
      toast.error(e?.response?.data?.message || 'Não foi possível carregar sua biblioteca');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [refreshKey]);

  const handleUpload = async (file: File) => {
    setUploading(true);
    try {
      // Probe duration only for video/audio
      let duration: number | undefined;
      if (file.type.startsWith('video/') || file.type.startsWith('audio/')) {
        const tag = file.type.startsWith('video/') ? 'video' : 'audio';
        duration = await new Promise<number>((resolve) => {
          const el = document.createElement(tag) as HTMLMediaElement;
          el.preload = 'metadata';
          el.onloadedmetadata = () => resolve(el.duration);
          el.onerror = () => resolve(0);
          el.src = URL.createObjectURL(file);
        });
      }
      const r = await videoStudioService.uploadOwnVideo(file, { title: file.name, duration });
      if (r.success && r.data) {
        toast.success(t('videoStudio.media.imported'));
        let asset = r.data;
        // Thumbnail best-effort pra vídeos (imagens já são a própria capa)
        if (file.type.startsWith('video/') && !asset.thumbnail_url) {
          try {
            const thumb = await captureVideoThumb(file);
            if (thumb) {
              const up = await videoStudioService.uploadImage(new File([thumb], 'thumb.jpg', { type: 'image/jpeg' }));
              if (up?.url) {
                const updated = await videoStudioService.update(asset.id, { thumbnail_url: up.url });
                if (updated.success && updated.data) asset = updated.data;
              }
            }
          } catch { /* sem thumb, card cai no <video preload> */ }
        }
        setAssets(prev => [asset, ...prev]);
      }
    } catch (e: any) {
      toast.error(e.response?.data?.message || t('videoStudio.media.uploadFail'));
    } finally {
      setUploading(false);
    }
  };

  const filtered = assets.filter(a => {
    const matchKind = filter === 'all' || detectKind(a) === filter;
    const matchSearch = !search || (a.title || '').toLowerCase().includes(search.toLowerCase());
    return matchKind && matchSearch;
  });

  return (
    <div className="space-y-3">
      {/* Add media menu (replaces the old single Import button + loose gallery buttons) */}
      <AddMediaMenu
        uploading={uploading}
        onUpload={() => fileRef.current?.click()}
        onPickGalleryVideo={() => onOpenGallery?.('video')}
        onPickGalleryImage={() => onOpenGallery?.('image')}
      />
      <input
        ref={fileRef} type="file"
        accept="video/*,audio/*,image/*"
        className="hidden"
        onChange={(e) => e.target.files?.[0] && handleUpload(e.target.files[0])}
      />

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-500" />
        <input
          type="text" value={search} onChange={(e) => setSearch(e.target.value)}
          placeholder="Buscar por nome do arquivo…"
          className="w-full pl-8 pr-3 py-1.5 rounded-lg bg-white dark:bg-gray-900/60 border border-gray-200 dark:border-gray-800 text-gray-900 dark:text-white text-xs focus:outline-none focus:border-primary-500/50"
        />
      </div>

      {/* Type filter pills */}
      <div className="flex gap-1">
        {([
          { v: 'all',   icon: null,                                       label: 'Tudo' },
          { v: 'video', icon: <Film className="w-3 h-3" />,               label: 'Vídeo' },
          { v: 'audio', icon: <Music className="w-3 h-3" />,              label: 'Áudio' },
          { v: 'image', icon: <ImageIcon className="w-3 h-3" />,          label: 'Imagem' }
        ] as const).map(opt => (
          <button key={opt.v}
            onClick={() => setFilter(opt.v)}
            className={`flex-1 px-2 py-1 rounded-md text-[10px] font-semibold inline-flex items-center justify-center gap-1 transition border ${
              filter === opt.v
                ? 'bg-primary-500/20 border-primary-500/50 text-primary-700 dark:text-primary-300'
                : 'bg-white dark:bg-gray-900/40 border-gray-200 dark:border-gray-800 text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-white/5'
            }`}
          >
            {opt.icon}{opt.label}
          </button>
        ))}
      </div>

      {/* Asset grid */}
      {loading ? (
        <div className="p-6 flex items-center justify-center"><Loader2 className="w-5 h-5 text-gray-500 animate-spin" /></div>
      ) : filtered.length === 0 ? (
        <div className="p-6 text-center text-xs text-gray-500 rounded-lg border border-dashed border-gray-200 dark:border-gray-800">
          <Film className="w-6 h-6 mx-auto mb-1 text-gray-700" />
          {t('videoStudio.media.empty')}
        </div>
      ) : (
        <div className="grid grid-cols-2 gap-2 max-h-[260px] overflow-y-auto pr-1">
          {filtered.map(a => {
            const kind = detectKind(a);
            const displayName = a.title || a.prompt?.slice(0, 40) || t('videoStudio.media.clip');
            return (
              <button
                key={a.id}
                onClick={() => onPick(a)}
                className="group relative rounded-lg overflow-hidden border border-gray-200 dark:border-gray-800 hover:border-primary-500/60 transition bg-white dark:bg-gray-900 flex flex-col"
                title={displayName}
              >
                {/* Thumbnail — 16:9 fixed, vertical media gets letterboxed (never blows up) */}
                <div className="relative aspect-video bg-black overflow-hidden flex items-center justify-center">
                  {kind === 'image' ? (
                    <img src={a.original_url || a.thumbnail_url || undefined} alt="" className="max-w-full max-h-full object-contain" />
                  ) : kind === 'audio' ? (
                    <div className="w-full h-full flex flex-col items-center justify-center bg-gradient-to-br from-emerald-500/30 to-green-500/30">
                      <Music className="w-7 h-7 text-emerald-200" />
                    </div>
                  ) : a.thumbnail_url ? (
                    <img src={a.thumbnail_url} alt="" className="max-w-full max-h-full object-contain" />
                  ) : (
                    <video src={a.original_url || undefined} preload="metadata" muted
                      className="max-w-full max-h-full object-contain" />
                  )}

                  {/* Type badge top-left */}
                  <span className={`absolute top-1 left-1 text-[9px] px-1.5 py-0.5 rounded inline-flex items-center gap-0.5 font-bold ${
                    kind === 'audio' ? 'bg-emerald-500/95 text-emerald-950'
                    : kind === 'image' ? 'bg-amber-500/95 text-amber-950'
                    : 'bg-cyan-500/95 text-cyan-950'
                  }`}>
                    {kind === 'audio' ? <><Music className="w-2.5 h-2.5" /> Áudio</>
                      : kind === 'image' ? <><ImageIcon className="w-2.5 h-2.5" /> Img</>
                      : <><Film className="w-2.5 h-2.5" /> Vídeo</>}
                  </span>

                  {/* Duration top-right */}
                  {a.duration_seconds && kind !== 'image' ? (
                    <span className="absolute top-1 right-1 text-[9px] px-1.5 py-0.5 rounded bg-black/70 text-white font-mono tabular-nums">
                      {Math.round(a.duration_seconds)}s
                    </span>
                  ) : null}

                  {/* Hover hint */}
                  <div className="absolute inset-0 bg-primary-600/0 group-hover:bg-primary-600/30 transition flex items-center justify-center opacity-0 group-hover:opacity-100">
                    <span className="text-[11px] text-white font-bold bg-primary-600 px-2 py-1 rounded shadow">+ Adicionar</span>
                  </div>
                </div>

                {/* Filename strip — always visible */}
                <div className="px-2 py-1.5 bg-gray-50 dark:bg-gray-900/80 border-t border-gray-200 dark:border-gray-800 text-left">
                  <p className="text-[11px] font-medium text-gray-900 dark:text-white truncate" title={displayName}>
                    {displayName}
                  </p>
                  <p className="text-[9px] text-gray-500 dark:text-gray-400 truncate font-mono">
                    {a.mime_type || 'unknown'}
                  </p>
                </div>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

// Single button → opens a popover with 3 sources of media
const AddMediaMenu: React.FC<{
  uploading: boolean;
  onUpload: () => void;
  onPickGalleryVideo: () => void;
  onPickGalleryImage: () => void;
}> = ({ uploading, onUpload, onPickGalleryVideo, onPickGalleryImage }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="relative">
      <button
        onClick={() => setOpen(o => !o)}
        disabled={uploading}
        className="w-full inline-flex items-center justify-center gap-2 px-3 py-2 rounded-lg bg-gradient-to-r from-primary-500 to-purple-600 text-white text-xs font-semibold hover:brightness-110 disabled:opacity-50 shadow-sm"
      >
        {uploading
          ? <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Enviando…</>
          : <><Upload className="w-3.5 h-3.5" /> Adicionar mídia</>}
      </button>

      {open && (
        <>
          <div className="fixed inset-0 z-30" onClick={() => setOpen(false)} />
          <div className="absolute left-0 right-0 top-full mt-1 z-40 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-lg shadow-xl py-1">
            <button
              onClick={() => { setOpen(false); onUpload(); }}
              className="w-full text-left px-3 py-2 hover:bg-gray-100 dark:hover:bg-white/5 text-gray-900 dark:text-white text-xs inline-flex items-center gap-2"
            >
              <Upload className="w-3.5 h-3.5 text-gray-500" />
              <div>
                <p className="font-semibold">Importar do computador</p>
                <p className="text-[10px] text-gray-500">Vídeo, áudio ou imagem</p>
              </div>
            </button>
            <button
              onClick={() => { setOpen(false); onPickGalleryVideo(); }}
              className="w-full text-left px-3 py-2 hover:bg-gray-100 dark:hover:bg-white/5 text-gray-900 dark:text-white text-xs inline-flex items-center gap-2"
            >
              <Film className="w-3.5 h-3.5 text-cyan-600 dark:text-cyan-400" />
              <div>
                <p className="font-semibold">Da galeria de vídeos</p>
                <p className="text-[10px] text-gray-500">Vídeos gerados ou enviados</p>
              </div>
            </button>
            <button
              onClick={() => { setOpen(false); onPickGalleryImage(); }}
              className="w-full text-left px-3 py-2 hover:bg-gray-100 dark:hover:bg-white/5 text-gray-900 dark:text-white text-xs inline-flex items-center gap-2"
            >
              <ImageIcon className="w-3.5 h-3.5 text-amber-600 dark:text-amber-400" />
              <div>
                <p className="font-semibold">Da galeria de imagens</p>
                <p className="text-[10px] text-gray-500">Imagens do Image Studio</p>
              </div>
            </button>
          </div>
        </>
      )}
    </div>
  );
};

// memo: o TimelineEditor re-renderiza ~10x/s durante a reprodução (tick do
// playhead); com props estáveis, a biblioteca (até 60 cards, alguns com <video>
// de fallback) fica de fora desse ciclo.
export default React.memo(MediaLibrary);
